import React, { useState, FormEvent } from "react";

interface Step {
  id: number;
  title: string;
  description: string;
}

const steps: Step[] = [
  { id: 1, title: "Organization Settings", description: "Set up basic information for your organization." },
  { id: 2, title: "User & Roles", description: "Configure admin users and their permissions." },
  { id: 3, title: "Integrations", description: "Connect third-party tools and platforms." },
  { id: 4, title: "Branding", description: "Upload logos, colors, and brand details." },
  { id: 5, title: "Review & Confirm", description: "Review your configuration and finalize setup." },
];

const AdminSetupWizard: React.FC = () => {
  const [activeStepId, setActiveStepId] = useState<number>(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const isCompleted = (id: number) => completedSteps.includes(id);

  const maxCompletedStep = completedSteps.length
    ? Math.max(...completedSteps)
    : 0;

  // A step is enabled if:
  // - It is step 1, OR
  // - The previous step has been completed
  const isStepEnabled = (stepId: number) => {
    if (stepId === 1) return true;
    return stepId <= maxCompletedStep + 1;
  };

  const handleSubmit =
    (stepId: number) =>
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      // TODO: Put your API call / form submission logic here

      if (!completedSteps.includes(stepId)) {
        setCompletedSteps((prev) => [...prev, stepId]);
      }

      const currentIndex = steps.findIndex((s) => s.id === stepId);
      const isLastStep = currentIndex === steps.length - 1;

      if (!isLastStep) {
        setActiveStepId(steps[currentIndex + 1].id);
      } else {
        // Final step finished — redirect to confirmation page
        // Option 1: Simple browser redirect
        window.location.href = "/admin/setup/confirmation";

        // Option 2 (if using React Router v6):
        // const navigate = useNavigate();
        // navigate("/admin/setup/confirmation");
      }
    };

  const goToStep = (id: number) => {
    setActiveStepId(id);
  };

  return (
    <div className="container my-4">
      <h1 className="mb-3">Admin Setup Guide</h1>
      <p className="text-muted mb-4">
        Complete each step to finish the configuration. You can always come back and update settings later.
      </p>

      <div className="accordion" id="adminSetupAccordion">
        {steps.map((step) => {
          const enabled = isStepEnabled(step.id);

          return (
            <div className="accordion-item" key={step.id}>
              <h2 className="accordion-header">
                <button
                  type="button"
                  className={
                    "accordion-button d-flex align-items-center " +
                    (activeStepId === step.id ? "" : "collapsed") +
                    (!enabled ? " disabled-step" : "")
                  }
                  onClick={() => enabled && goToStep(step.id)}
                  disabled={!enabled}
                >
                  <span className="me-2 fw-semibold">Step {step.id}:</span>
                  <span>{step.title}</span>
                  {isCompleted(step.id) && (
                    <span className="badge bg-success ms-3">Completed</span>
                  )}
                  {!enabled && !isCompleted(step.id) && (
                    <span className="badge bg-secondary ms-3">Locked</span>
                  )}
                </button>
              </h2>
              <div
                className={
                  "accordion-collapse collapse " +
                  (activeStepId === step.id ? "show" : "")
                }
              >
                <div className="accordion-body">
                  <p className="text-muted">{step.description}</p>

                  {/* One FORM per step */}
                  <form onSubmit={handleSubmit(step.id)}>
                    {/* Example fields – replace with your real ones per step */}
                    <div className="mb-3">
                      <label className="form-label">
                        {step.id === 1 && "Organization Name"}
                        {step.id === 2 && "Primary Admin Email"}
                        {step.id === 3 && "Integration Key / Token"}
                        {step.id === 4 && "Primary Brand Color"}
                        {step.id === 5 && "Confirmation Notes"}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={
                          step.id === 1
                            ? "e.g. Southern Nevada Golf Association"
                            : step.id === 2
                            ? "admin@example.com"
                            : step.id === 3
                            ? "Paste your API key or token"
                            : step.id === 4
                            ? "#123456"
                            : "Any final notes or comments"
                        }
                        required
                      />
                    </div>

                    {/* You can add more fields per step */}
                    {step.id === 2 && (
                      <div className="mb-3">
                        <label className="form-label">Role</label>
                        <select className="form-select" required>
                          <option value="">Select a role...</option>
                          <option value="super_admin">Super Admin</option>
                          <option value="admin">Admin</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </div>
                    )}

                    {step.id === 4 && (
                      <div className="mb-3">
                        <label className="form-label">Logo URL</label>
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://example.com/logo.png"
                        />
                      </div>
                    )}

                    <div className="d-flex justify-content-between">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={step.id === 1}
                        onClick={() => goToStep(step.id - 1)}
                      >
                        Back
                      </button>

                      <button type="submit" className="btn btn-primary">
                        {step.id === steps.length ? "Finish Setup" : "Save & Continue"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Optional: summary footer */}
      <div className="mt-4">
        <small className="text-muted">
          Completed {completedSteps.length} of {steps.length} steps.
        </small>
      </div>

      {/* Optional small style tweak for disabled accordion buttons */}
      <style>{`
        .accordion-button.disabled-step {
          cursor: not-allowed;
          opacity: 0.6;
        }
      `}</style>
    </div>
  );
};

export default AdminSetupWizard;
